import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo, faSave } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { ViewType } from 'src/app/models/enums/view-type';
import { IView } from 'src/app/models/interfaces/view.interface';
import { FormMode } from '../../../controls/common/form-mode';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { Controls } from '../../../controls/forms/managed-form/ngx-sub-form-utils';
import { DataInput } from '../../../controls/forms/management-form/form.decorators';
import { ManagementFormComponent } from '../../../controls/forms/management-form/management-form';
import { ViewFormContext } from '../view-form-context';

@Component({
    selector: 'app-view-form',
    templateUrl: './view-form.component.html',
    styleUrls: ['./view-form.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        FaIconComponent,
        MatTooltipModule,
        MatSelectModule,
        MatOptionModule,
        MatButtonModule,
        NgClass,
    ]
})
export class ViewFormComponent extends ManagementFormComponent<IView> implements OnInit, OnChanges {
    public ViewType = ViewType;

    public faSave = faSave;
    public faCircleInfo = faCircleInfo;

    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Infos de l'utilisateur à afficher dans le formulaire.
     */
    @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('view')
    public dataInput: Required<IView> | null | undefined;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: ViewFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<ViewFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: ViewFormContext) {
        this._context$.next(value);
    }

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('viewUpdated')
    public dataOutput: EventEmitter<IView> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    public viewTypes = [];

    /**Constructeur */
    constructor() {
        super();
    }

    /**
     * Initialise le composant.
     */
    override ngOnInit() {
        super.ngOnInit();

        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.view);
            this.updateFormFieldState();
        });

        this.viewTypes.push({ id: 1, value: 'Bannettes' });
        this.viewTypes.push({ id: 2, value: 'Workflow' });
        if (this._context?.mode !== FormMode.Creation) {
            this.viewTypes.push({ id: 3, value: 'Bannettes (archivée)' });
            this.viewTypes.push({ id: 4, value: 'Workflow (archivée)' });
        }
    }

    /**
     * Utilisé pour rendre le champ id read-only
     */
    ngOnChanges() {
        this.updateFormFieldState();
    }

    private updateFormFieldState() {
        this.formGroup.get('id').disable();

        //Rend le champ type read-only en édition
        if (this._context?.mode === FormMode.Edition) {
            this.formGroup.get('viewType').disable();
        }

        // Rend le champ defaultSendingAddressId, delayBeforeArchiving et smsOadc read-only pour les vues de type archive
        if (this._context?.view && (this._context?.view.viewType === ViewType.ArchivingBox || this._context?.view.viewType === ViewType.ArchivingWorkflow)) {
            this.formGroup.get('defaultSendingAddressId').disable();
            this.formGroup.get('delayBeforeArchiving').disable();
            this.formGroup.get('smsOadc').disable();
        }

        // Rend le champ delayBeforeArchiving read-only pour les vues 'active' dont l'archivage n'est pas actif
        if (this._context?.view && this._context?.view.archivingEnabled === false) {
            this.formGroup.get('delayBeforeArchiving').disable();
        }
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<IView> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            name: new UntypedFormControl(null, Validators.required),
            viewType: new UntypedFormControl(null, Validators.required),
            defaultSendingAddressId: new UntypedFormControl(null),
            parentId: new UntypedFormControl(null),
            isArchive: new UntypedFormControl(null),
            updateDate: new UntypedFormControl(null),
            archivingEnabled: new UntypedFormControl(null),
            delayBeforeArchiving: new UntypedFormControl(null),
            smsOadc: new UntypedFormControl(null),
        };
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        // Force le type car il n'est pas fourni à l'API car le champ est disabled
        this.dataValue.viewType = this.formGroup.get('viewType').value;
        this.dataValue.parentId = this._context.view.parentId;
        super.manualSave();
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
